// frontend/src/modules/payroll/components/CreateRunDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { payrollApi } from '../api';
import type { PayrollRun } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (run: PayrollRun) => void;
}

/** Default to the previous calendar month: [first-of-prev-month, last-of-prev-month]. */
function defaultPeriod(): { start: string; end: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();           // 0-based
  const firstPrev = new Date(Date.UTC(y, m - 1, 1));
  const lastPrev  = new Date(Date.UTC(y, m, 0));
  return { start: firstPrev.toISOString().slice(0, 10), end: lastPrev.toISOString().slice(0, 10) };
}

export function CreateRunDialog({ open, onOpenChange, onCreated }: Props) {
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      const { start: s, end: e } = defaultPeriod();
      setStart(s); setEnd(e); setNotes('');
    }
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!start || !end) { toast.error('Pick a period'); return; }
    setBusy(true);
    try {
      const run = await payrollApi.createRun({ period_start: start, period_end: end, notes: notes || null });
      onCreated(run);
    } catch (err: any) {
      toast.error('Create failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New payroll run</DialogTitle>
          <DialogDescription>Defaults to last month — adjust if needed.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Period start</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Period end</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional internal note" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create run'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
